import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import LoadingSpinner from '@/components/LoadingSpinner';
import { 
  ChevronLeft,
  Search,
  Heart,
  MapPin,
  Star,
  Clock,
  Users,
  Filter,
  Plus,
  Minus
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useGuestApi } from '@/hooks/useRoleBasedApi';
import UnifiedMobileFooter from '@/components/UnifiedMobileFooter';

interface Location {
  id: number;
  name: string;
  address?: string;
  type?: string;
  services?: string[];
  amenities?: string[];
  rating?: number;
  distance?: number;
}

const FavouritesPage: React.FC = () => {
  const { user } = useAuth();
  const guestApi = useGuestApi();
  
  const [searchTerm, setSearchTerm] = useState("");
  const [showFavouritesOnly, setShowFavouritesOnly] = useState(true);
  const [favouriteLocations, setFavouriteLocations] = useState<Location[]>([]);
  const [allLocations, setAllLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);

  // Load favourite location IDs from localStorage
  const getFavouriteLocationIds = (): number[] => {
    try {
      const stored = localStorage.getItem('favourite_locations');
      return JSON.parse(stored || '[]');
    } catch {
      return [];
    }
  };

  // Load all locations and filter favourites
  const loadLocations = async () => {
    try {
      setLoading(true);
      
      // Fetch all locations
      const locationsData = await guestApi.get('/locations');
      setAllLocations(locationsData.locations || []);
      
      // Get favourite IDs
      const favouriteIds = getFavouriteLocationIds();
      
      // Filter to show only favourite locations
      const favourites = locationsData.locations?.filter((loc: Location) => 
        favouriteIds.includes(loc.id)
      ) || [];
      
      setFavouriteLocations(favourites);
      
    } catch (error) {
      console.error('Failed to load locations:', error);
      
      // Report specific backend issues
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch')) {
          console.error('BACKEND ISSUE: Network error - backend may be down or CORS not configured');
        } else if (error.message.includes('401')) {
          console.error('BACKEND ISSUE: Authentication error - user session may be invalid');
        } else if (error.message.includes('404')) {
          console.error('BACKEND ISSUE: API endpoint not found - check if /locations endpoint exists');
        } else if (error.message.includes('500')) {
          console.error('BACKEND ISSUE: Server error - backend may have crashed');
        } else {
          console.error('BACKEND ISSUE:', error.message);
        }
      }
      
      // Set empty states for failed API calls
      setAllLocations([]);
      setFavouriteLocations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadLocations();
    }
  }, [user]);

  // Toggle favourite status
  const toggleFavourite = (locationId: number) => {
    const currentFavourites = getFavouriteLocationIds();
    const isFavourite = currentFavourites.includes(locationId);
    
    let newFavourites;
    if (isFavourite) {
      newFavourites = currentFavourites.filter(id => id !== locationId);
    } else {
      newFavourites = [...currentFavourites, locationId];
    }
    
    localStorage.setItem('favourite_locations', JSON.stringify(newFavourites));
    
    // Update the displayed favourites
    const updatedFavourites = allLocations.filter(loc => newFavourites.includes(loc.id));
    setFavouriteLocations(updatedFavourites);
  };

  // Filter locations based on search term and favourites toggle
  const getFilteredLocations = () => {
    const locationsToFilter = showFavouritesOnly ? favouriteLocations : allLocations;
    return locationsToFilter.filter(location =>
      location.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      location.address?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filteredLocations = getFilteredLocations();

  const clearSearch = () => {
    setSearchTerm("");
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Favourites | SaunaDip</title>
          <meta name="description" content="Manage your favourite sauna locations" />
        </Helmet>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <LoadingSpinner size="xl" text="Loading favourites..." />
        </div>
        <UnifiedMobileFooter />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Favourites | SaunaDip</title>
        <meta name="description" content="Your favourite sauna and cold plunge locations" />
      </Helmet>
      
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 py-3">
          <div className="flex items-center gap-3">
            <h1 className="text-lg font-semibold text-gray-900">Favourites</h1>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {/* Favourites Toggle */}
          <div className="flex justify-center">
            <Button
              variant={showFavouritesOnly ? "default" : "outline"}
              size="sm"
              onClick={() => setShowFavouritesOnly(!showFavouritesOnly)}
              className={`rounded-full px-4 py-2 ${
                showFavouritesOnly 
                  ? 'bg-sauna-orange text-white' 
                  : 'bg-white text-gray-700 border-sauna-orange'
              }`}
            >
              <Star className="w-4 h-4 mr-2" />
              Show Favourites Only
            </Button>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              type="text"
              placeholder="Search locations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-10 rounded-full"
            />
            {searchTerm && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearSearch}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
              >
                ×
              </Button>
            )}
          </div>

          {/* Location List */}
          <div className="space-y-3">
            {filteredLocations.length === 0 ? (
              <Card>
                <CardContent className="text-center py-12">
                  <Heart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">
                    {searchTerm ? 'No locations found' : showFavouritesOnly ? 'No favourites yet' : 'No locations available'}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {searchTerm 
                      ? 'Try adjusting your search terms.'
                      : showFavouritesOnly 
                        ? 'Start adding locations to your favourites.'
                        : 'No locations are currently available.'
                    }
                  </p>
                  {!searchTerm && showFavouritesOnly && (
                    <Button 
                      variant="outline"
                      onClick={() => setShowFavouritesOnly(false)}
                    >
                      Browse All Locations
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              filteredLocations.map((location) => {
                const isFavourite = getFavouriteLocationIds().includes(location.id);
                return (
                  <Card key={location.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        {/* Location Icon */}
                        <div className="w-10 h-10 bg-sauna-orange/20 rounded-full flex items-center justify-center">
                          <MapPin className="w-5 h-5 text-sauna-orange" />
                        </div>
                        
                        {/* Location Details */}
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1">{location.name}</h3>
                          <p className="text-sm text-gray-500">
                            {showFavouritesOnly ? 'Favourite location' : 'Available location'}
                          </p>
                        </div>
                        
                        {/* Favourite Toggle */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleFavourite(location.id)}
                          className={`p-2 ${
                            isFavourite 
                              ? 'text-red-500 hover:text-red-700' 
                              : 'text-gray-400 hover:text-sauna-orange'
                          }`}
                        >
                          <Heart className={`w-5 h-5 ${isFavourite ? 'fill-current' : ''}`} />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </div>
      </div>
      <UnifiedMobileFooter />
    </>
  );
};

export default FavouritesPage; 